import React from 'react';

/**
 * Big level meter for the DAW. Shows a vertical bar with dB scale markings,
 * color-coded zones (green / yellow / red), and a numeric peak readout so
 * the engineer can see at a glance whether signal is hot, warm, or cold.
 *
 * Props:
 *   label      — "IN" or "OUT" (string)
 *   level      — 0-100 (already mapped from dBFS in StudioSession)
 *   peak       — optional 0-100 hold-peak line
 *   subtitle   — small caption under the label (e.g. "Input" / "Master")
 */
const BigLevelMeter = ({ label = 'OUT', level = 0, peak = 0, subtitle = '' }) => {
  // Map 0-100 back to a rough dB value for the numeric readout.
  const dbValue = level <= 0 ? -Infinity : ((level / 100) * 60) - 60;
  const dbLabel =
    !isFinite(dbValue) ? '—' :
    dbValue >= 0 ? `${dbValue.toFixed(1)} dB` :
    `${dbValue.toFixed(1)} dB`;

  const status =
    level >= 92 ? { text: 'HOT', color: 'text-red-500', bg: 'bg-red-500/20 border-red-500/60' } :
    level >= 75 ? { text: 'WARM', color: 'text-yellow-400', bg: 'bg-yellow-500/15 border-yellow-500/50' } :
    level >= 15 ? { text: 'GOOD', color: 'text-green-400', bg: 'bg-green-500/10 border-green-500/40' } :
                  { text: 'COLD', color: 'text-sky-400', bg: 'bg-sky-500/10 border-sky-500/40' };

  // dB scale marks to render alongside the bar (0 at top, -60 at bottom)
  const scale = [
    { db: 0,   top: 0   },
    { db: -6,  top: 10  },
    { db: -12, top: 20  },
    { db: -20, top: 33  },
    { db: -30, top: 50  },
    { db: -40, top: 66  },
    { db: -60, top: 100 },
  ];

  return (
    <div className="flex flex-col items-center gap-2 select-none">
      <div className="text-lg font-black tracking-widest text-white">{label}</div>
      {subtitle && <div className="text-xs text-gray-400 -mt-1.5">{subtitle}</div>}
      <div className="flex gap-2">
        {/* dB scale markings */}
        <div className="relative w-8 h-48 text-[11px] text-gray-500 font-mono">
          {scale.map((s) => (
            <div
              key={s.db}
              className="absolute right-0 -translate-y-1/2 flex items-center gap-1"
              style={{ top: `${s.top}%` }}
            >
              <span>{s.db === 0 ? '0' : s.db}</span>
              <span className="w-1.5 h-px bg-gray-600" />
            </div>
          ))}
        </div>
        {/* The bar itself */}
        <div className="relative w-8 h-48 bg-black rounded border border-[#333] overflow-hidden">
          {/* Colored zones as background so they're always visible */}
          <div className="absolute inset-x-0 top-0 h-[10%] bg-red-500/10" />
          <div className="absolute inset-x-0 top-[10%] h-[15%] bg-yellow-500/10" />
          <div className="absolute inset-x-0 top-[25%] h-[75%] bg-green-500/10" />
          {/* Live level fill */}
          <div
            className="absolute inset-x-0 bottom-0 transition-[height] duration-75"
            style={{
              height: `${Math.max(0, Math.min(100, level))}%`,
              background: 'linear-gradient(to top, #22c55e 0%, #22c55e 60%, #eab308 75%, #ef4444 92%)',
            }}
          />
          {/* Peak hold line */}
          {peak > 0 && (
            <div
              className="absolute inset-x-0 h-0.5 bg-white/90"
              style={{ bottom: `${Math.min(100, peak)}%` }}
            />
          )}
        </div>
      </div>
      {/* Numeric readout + hot/warm/good/cold badge */}
      <div className={`px-2 py-1 rounded-md border ${status.bg} w-full`}>
        <div className={`text-center font-mono text-sm ${status.color}`}>{dbLabel}</div>
        <div className={`text-center text-[11px] font-black tracking-widest ${status.color}`}>{status.text}</div>
      </div>
    </div>
  );
};

export default BigLevelMeter;
